import React, { Component } from 'react';
import PropTypes from 'prop-types';
import 'isomorphic-fetch';
import { translate } from 'react-i18next';
import { withGoogleMap, GoogleMap, OverlayView } from 'react-google-maps';
import withScriptjs from 'react-google-maps/lib/async/withScriptjs';

import {
  buildApiParams,
  getPriceInCurrency,
  formatPrice,
  debouncer
} from '../../../utils';
import config from '../../../config';
import { CURRENCIES } from '../../../constants';

import MapInfoWindow from './MapInfoWindow';

import './SearchMap.css';

const AsyncGoogleMap = withScriptjs(withGoogleMap(props => (
  <GoogleMap
    ref={props.mapLoad}
    zoom={props.defaultZoom}
    center={props.center}
    options={{
      clickableIcons: false,
      disableDoubleClickZoom: true,
      mapTypeControl: false,
      fullscreenControl: false,
      rotateControl: false,
      streetViewControl: false,
      minZoom: 9,
      maxZoom: 20
    }}
    onBoundsChanged={props.boundsChanged}
  >
    {props.children}
  </GoogleMap>
)));

const GEOCODER_BASE_URL = `${config.apiRoot}geocoder/`;
const DEFAULT_CENTER = { lat: 41.387668, lng: 2.172583 };

function getPixelPositionOffset(width, height) {
  return {
    x: -(width / 2),
    y: -height - 4
  };
}

class SearchMap extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      activeCompany: null
    };

    this.mapLoadHanlder = this.onMapLoad.bind(this);
    this.boundsChangedHanlder = this.onBoundsChanged.bind(this);
    this.infoWindowClosehandler = this.onInfoWindowClose.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (this.map) {
      if (
        JSON.stringify(this.props.initialState) !== JSON.stringify(nextProps.initialState) ||
        typeof this.props.countries !== typeof nextProps.countries
      ) {
        this.setState({ activeCompany: null }, () => {
          this.props.clearSearchResultsMap();
        });

        this.getCityMapBounds(this.props.countries, nextProps.initialState);
      }
    }
  }

  componentWillUnmount() {
    if (this.boundsChangedDebouncer) {
      clearTimeout(this.boundsChangedDebouncer);
      this.boundsChangedDebouncer = null;
    }
  }

  onMapLoad(map) {
    this.map = map;
    this.getCityMapBounds(this.props.countries || [], this.props.initialState);
  }

  onBoundsChanged() {
    this.boundsChangedDebouncer = debouncer(this.boundsChangedDebouncer, () => {
      if (!this.map) {
        return;
      }

      const bounds = this.map.getBounds();
      this.loadBoundsSearchResults(bounds);
    });
  }

  getCityMapBounds(countries, initialState) {
    if (!window.google) {
      return;
    }

    const country = countries.find(country => country.id === initialState.country);

    if (!country) {
      return;
    }

    const city = country.cities.find(city => city.id === initialState.pickUpCity);

    if (!city) {
      return;
    }

    fetch(`${GEOCODER_BASE_URL}?address=${country.name},${city.name}`)
      .then(response => {
        if (response.ok) {return response.json().then(json => json);}
        return null;
      })
      .then(json => {
        if (!json || json.status !== 'OK' || !json.results[0] || !this.map) {return;}

        const { bounds } = json.results[0].geometry;
        this.map.fitBounds(new window.google.maps.LatLngBounds(bounds.southwest, bounds.northeast));
      });
  }

  loadBoundsSearchResults(bounds) {
    if (!bounds) {
      return;
    }

    const searchParams = Object.assign({}, this.props.initialState, {
      x1: bounds.getSouthWest().lat(),
      y1: bounds.getSouthWest().lng(),
      x2: bounds.getNorthEast().lat(),
      y2: bounds.getNorthEast().lng()
    });

    delete searchParams.country;
    delete searchParams.dropOfCity;
    delete searchParams.pickUpCity;
    delete searchParams.returnAtSameCity;
    delete searchParams.ordering;

    this.props.loadSearchResultsMap(buildApiParams(searchParams));
  }

  onMarkerClick(activeCompany) {
    this.setState({ activeCompany }, () => {

      // this.map.panTo({ lat: activeCompany.lat, lng: activeCompany.lng });
    });
  }

  onInfoWindowClose(event) {
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }

    this.setState({ activeCompany: null });
  }

  render() {
    const { activeCompany } = this.state;
    const currency = CURRENCIES.find(c => c.value === this.props.currency);
    const symbol = currency && currency.symbol;
    const companies = this.props.items && this.props.items
      .reduce((prev, bike) => {
        let company = prev.find(company => company.id === bike.company.id);
        const price = getPriceInCurrency(bike.price, this.props.currency);
        const salesPrice = getPriceInCurrency(bike.salesPrice, this.props.currency);
        bike.displayPrice = `${formatPrice(salesPrice)} ${symbol}`;

        if (!company) {
          company = Object.assign({}, bike.company, {
            bikes: [],
            minPrice: price
          });
          prev.push(company);
        }

        company.minPrice = Math.min(company.minPrice, price, salesPrice);
        company.bikes.push(bike);

        return prev;
      }, [])
      .filter(company =>
        !!company.lat && !!company.lng && (!activeCompany || company.id !== activeCompany.id)
      );

    return (
      <div className="search-map">
        <AsyncGoogleMap
          googleMapURL={`https://maps.googleapis.com/maps/api/js?v=3.exp&key=${config.GOOGLE_MAP_API_KEY}`}
          loadingElement={<div style={{ height: 500 }} />}
          containerElement={<div style={{ height: 500 }} />}
          mapElement={<div style={{ height: 500 }} />}
          center={DEFAULT_CENTER}
          mapLoad={this.mapLoadHanlder}
          boundsChanged={this.boundsChangedHanlder}
        >
          {!!companies && companies.map((company) =>
            <OverlayView
              key={company.id}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              position={{ lat: company.lat, lng: company.lng }}
              getPixelPositionOffset={getPixelPositionOffset}
            >
              <div
                className="custom-map-marker"
                onClick={this.onMarkerClick.bind(this, company)}
              >
                {formatPrice(company.minPrice)} <strong>{symbol}</strong>
              </div>
            </OverlayView>
          )}

          {!!activeCompany && (
            <OverlayView
              ref="infoWindow"
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              position={{
                lat: activeCompany.lat,
                lng: activeCompany.lng
              }}
              getPixelPositionOffset={getPixelPositionOffset}
            >
              <MapInfoWindow
                company={activeCompany}
                symbol={symbol}
                onSelect={this.props.onSelect}
                onClose={this.infoWindowClosehandler}
              />
            </OverlayView>
          )}
        </AsyncGoogleMap>
      </div>
    );
  }
}

SearchMap.propTypes = {
  countries: PropTypes.array.isRequired,
  initialState: PropTypes.object.isRequired,
  isFetching: PropTypes.bool,
  items: PropTypes.array,
  loadSearchResultsMap: PropTypes.func.isRequired,
  clearSearchResultsMap: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired
};

export default translate(['translation'])(SearchMap);



// WEBPACK FOOTER //
// ./src/components/SearchPage/SearchMap/SearchMap.js